import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, ShieldCheck, FlaskConical, Microscope, Cpu, ArrowRight, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import Molecule3D from "@/components/Molecule3D";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

const TICKER = [
    "BPC-157",
    "TB-500",
    "SEMAGLUTIDE",
    "TIRZEPATIDE",
    "GHK-CU",
    "SS-31",
    "IPAMORELIN",
    "CJC-1295",
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [query, setQuery] = useState("");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const tickerItems = [
        ...TICKER.map((name) => ({ label: name })),
        { label: t("hero.tickerGuideLabel"), href: "/nexus-peptides-101-guide.pdf" },
    ];

    useEffect(() => {
        api.get("/products", { params: { featured: true } })
            .then((r) => {
                setFeatured((r.data || []).slice(0, 4));
            })
            .catch(() => {});
    }, []);

    const submitSearch = (e) => {
        e?.preventDefault();
        const q = query.trim();
        navigate(q ? `/shop?q=${encodeURIComponent(q)}` : "/shop");
    };

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[calc(100vh-5rem)] overflow-hidden bg-gradient-to-b from-[#e9f6fd] via-white to-white">
                <div className="absolute -top-32 -left-32 w-[540px] h-[540px] rounded-full bg-[#00aeef]/10 blur-3xl pointer-events-none" />
                <div className="absolute top-1/3 right-0 w-[420px] h-[420px] rounded-full bg-[#0e7490]/[0.07] blur-3xl pointer-events-none" />

                {/* Molecule canvas — on mobile sits in upper half only; on desktop fills right side */}
                <div className="absolute top-0 right-0 left-0 lg:left-auto h-[55vh] lg:h-auto lg:inset-y-0 lg:w-[85%] xl:w-[88%] pointer-events-auto">
                    <Molecule3D />
                </div>

                {/* Gradient masks: vertical fade on mobile, horizontal on desktop */}
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-b lg:bg-gradient-to-r from-transparent via-white/70 to-white lg:from-white lg:via-white/85 lg:to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none bg-gradient-to-b from-transparent to-white" />

                <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 pt-[42vh] sm:pt-[44vh] lg:pt-24 pb-20 lg:pb-32 pointer-events-none">
                    <div className="max-w-2xl pointer-events-auto">
                        <div
                            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#0077b6] border border-[#00aeef]/30 px-3 py-1.5 mb-8 bg-white/40 backdrop-blur"
                            data-testid="hero-badge"
                        >
                            <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full nx-pulse" />
                            {t("hero.badge")}
                        </div>

                        <h1 className="font-display font-extrabold uppercase tracking-tighter text-[40px] sm:text-6xl lg:text-6xl xl:text-7xl leading-[0.95] text-neutral-900" data-testid="hero-headline">
                            {t("hero.headlineLine1")}
                            <br />
                            {t("hero.headlineLine2")}
                            <span className="text-[#0077b6] "> {t("hero.headlineAccent")}</span>
                            <br />
                            {t("hero.headlineLine3")}
                        </h1>

                        <p className="mt-8 text-base lg:text-lg text-neutral-600 max-w-xl leading-relaxed">
                            {t("hero.body")}
                        </p>

                        {/* Product search — top-of-page entry into the catalog */}
                        <form onSubmit={submitSearch} className="mt-8 max-w-xl">
                            <label
                                htmlFor="home-product-search"
                                className="block text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2"
                            >
                                {t("hero.searchLabel")}
                            </label>
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
                                <input
                                    id="home-product-search"
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("hero.searchPlaceholder")}
                                    className="w-full bg-white/60 backdrop-blur border border-neutral-300 focus:border-[#00aeef] focus:outline-none focus:ring-1 focus:ring-[#00aeef]/40 pl-11 pr-24 sm:pr-32 py-3.5 sm:py-4 text-sm text-neutral-900 placeholder:text-neutral-500 rounded-lg transition-colors"
                                    data-testid="home-product-search-input"
                                    autoComplete="off"
                                />
                                <button
                                    type="submit"
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-[10px] px-3 sm:px-4 py-2 sm:py-2.5 transition-colors"
                                    data-testid="home-product-search-submit"
                                >
                                    {t("hero.searchButton")}
                                </button>
                            </div>
                        </form>

                        <div className="mt-6 flex flex-wrap items-center gap-3 sm:gap-4">
                            <Link
                                to="/shop"
                                className="group inline-flex items-center gap-3 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs px-5 sm:px-7 py-3.5 sm:py-4 transition-colors"
                                data-testid="hero-shop-btn"
                            >
                                {t("hero.exploreCatalog")}
                                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                            </Link>
                            <Link
                                to="/research"
                                className="inline-flex items-center gap-3 border border-neutral-300 hover:border-[#0089c8] rounded-lg text-neutral-900 hover:text-black font-semibold uppercase tracking-[0.18em] text-xs px-5 sm:px-6 py-3.5 sm:py-4 transition-colors backdrop-blur"
                                data-testid="hero-research-btn"
                            >
                                {t("hero.viewResearch")}
                            </Link>
                        </div>

                        <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl">
                            <div>
                                <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#0077b6] ">
                                    ≥99%
                                </div>
                                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-neutral-500 mt-1">
                                    {t("hero.statPurity")}
                                </div>
                            </div>
                            <div>
                                <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#0077b6] ">
                                    19
                                </div>
                                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-neutral-500 mt-1">
                                    {t("hero.statCompounds")}
                                </div>
                            </div>
                            <div>
                                <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#0077b6] ">
                                    24/7
                                </div>
                                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-neutral-500 mt-1">
                                    {t("hero.statColdChain")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hidden lg:block absolute bottom-6 right-6 z-10 text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-mono pointer-events-none">
                    {t("hero.dragHint")}
                </div>
            </section>

            {/* TICKER */}
            <section className="border-y border-neutral-200 bg-white overflow-hidden">
                <div className="flex nx-marquee py-4 whitespace-nowrap">
                    {[...tickerItems, ...tickerItems].map((item, i) =>
                        item.href ? (
                            <a
                                key={`guide-${i}`}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 px-8 text-xs font-mono uppercase tracking-[0.22em] text-[#0077b6] font-bold hover:text-[#0089c8] transition-colors"
                                data-testid="ticker-guide-link"
                            >
                                <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full" />
                                {item.label}
                            </a>
                        ) : (
                            <span
                                key={`${item.label}-${i}`}
                                className="inline-flex items-center gap-3 px-8 text-xs font-mono uppercase tracking-[0.22em] text-neutral-600"
                            >
                                <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full" />
                                {item.label}
                            </span>
                        )
                    )}
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-16 sm:py-24 lg:py-32">
                <div className="flex items-end justify-between mb-8 sm:mb-12">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">
                            {t("hero.sectionCatalog")}
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold uppercase tracking-tighter">
                            {t("hero.signature")}
                        </h2>
                    </div>
                    <Link
                        to="/shop"
                        className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-600 hover:text-black transition-colors"
                        data-testid="view-all-products"
                    >
                        {t("hero.viewFullCatalog")} <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {featured.map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            </section>

            {/* PROCESS / WHY */}
            <section className="border-t border-neutral-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 items-center">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">
                            {t("hero.sectionMethod")}
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold uppercase tracking-tighter mb-6">
                            {t("hero.methodHeading1")}
                            <br />
                            {t("hero.methodHeading2")}
                            <span className="text-[#0077b6]"> {t("hero.methodAccent")}</span>.
                        </h2>
                        <p className="text-neutral-600 max-w-xl leading-relaxed mb-10">
                            {t("hero.methodBody")}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#00aeef]/20 border border-[#00aeef]/20 rounded-xl overflow-hidden">
                            {[
                                {
                                    icon: FlaskConical,
                                    title: t("process.synthesis.title"),
                                    desc: t("process.synthesis.desc"),
                                },
                                {
                                    icon: Microscope,
                                    title: t("process.hplc.title"),
                                    desc: t("process.hplc.desc"),
                                },
                                {
                                    icon: ShieldCheck,
                                    title: t("process.screening.title"),
                                    desc: t("process.screening.desc"),
                                },
                                {
                                    icon: Cpu,
                                    title: t("process.coldchain.title"),
                                    desc: t("process.coldchain.desc"),
                                },
                            ].map((it, i) => (
                                <div key={it.title} className="bg-[#ffffff] p-7" data-testid={`process-card-${i}`}>
                                    <it.icon className="w-5 h-5 text-[#0077b6] mb-4" />
                                    <div className="font-display font-bold text-base mb-2">{it.title}</div>
                                    <div className="text-xs text-neutral-500 leading-relaxed">{it.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative aspect-square border border-neutral-200 overflow-hidden rounded-2xl">
                        <img
                            src="https://static.prod-images.emergentagent.com/jobs/d5c608c5-d491-4850-bad2-86a0f6155910/images/7c6c355a68885ffa06a11357ecda765a5c0089e4aa9d9c8447a336c2cba2b983.png"
                            alt="Partner lab"
                            className="w-full h-full object-cover"
                            style={{ filter: "saturate(0.7) contrast(1.05)" }}
                        />
                        <div className="absolute inset-0 bg-[#00aeef] mix-blend-color opacity-30 pointer-events-none" />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#00aeef]/10 to-transparent pointer-events-none" />
                        <div className="absolute bottom-6 left-6 right-6 bg-white/70 backdrop-blur border border-neutral-200 p-5">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] mb-2">
                                {t("hero.partnerLabSpec")}
                            </div>
                            <div className="font-display font-bold text-lg leading-tight">
                                {t("hero.partnerLabSpecCaption")}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-16 sm:py-24">
                <div className="relative border border-[#00aeef]/40 bg-gradient-to-br from-[#eaf7fe] via-white to-white p-8 sm:p-10 lg:p-20 nx-glow nx-ticks overflow-hidden rounded-2xl">
                    <div className="absolute inset-0 nx-grid-bg opacity-40" />
                    <div className="relative">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-4">
                            {t("hero.ctaEyebrow")}
                        </div>
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-extrabold uppercase tracking-tighter max-w-3xl">
                            {t("hero.ctaHeading")}
                        </h2>
                        <div className="mt-8 flex flex-wrap gap-3 sm:gap-4">
                            <Link
                                to="/shop"
                                className="inline-flex items-center gap-3 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs px-5 sm:px-7 py-3.5 sm:py-4"
                                data-testid="cta-shop"
                            >
                                {t("hero.ctaBrowse")}
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/bulk-quote"
                                className="inline-flex items-center gap-3 border border-neutral-300 hover:border-[#0089c8] rounded-lg hover:text-black text-neutral-900 font-semibold uppercase tracking-[0.18em] text-xs px-5 sm:px-6 py-3.5 sm:py-4"
                                data-testid="cta-bulk"
                            >
                                {t("hero.ctaBulk")}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
